import { useState, useEffect, useRef } from "react";

/* ─── SHARED CSS ─────────────────────────────────────────────────────────── */
const SHARED_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600;700;900&family=JetBrains+Mono:ital,wght@0,300;0,400;0,600;1,300&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg:#03080e;--panel:#070f18;--panel2:#0b1621;
  --border:rgba(0,220,130,0.13);--border2:rgba(0,220,130,0.25);
  --green:#00dc82;--green-dim:rgba(0,220,130,0.10);--green-dim2:rgba(0,220,130,0.18);
  --blue:#0af;--yellow:#ffc93c;--red:#ff3d5a;
  --text:#c8e8d8;--muted:#3a6650;--muted2:#5a8870;
  --ff-display:'Orbitron',monospace;--ff-mono:'JetBrains Mono',monospace;
}
html,body,#root{background:var(--bg);color:var(--text);font-family:var(--ff-mono);font-size:13px;min-height:100vh}
body::after{content:'';position:fixed;inset:0;pointer-events:none;z-index:9999;
  background:repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.025) 2px,rgba(0,0,0,0.025) 4px)}
*::-webkit-scrollbar{width:3px;height:3px}
*::-webkit-scrollbar-track{background:transparent}
*::-webkit-scrollbar-thumb{background:var(--border2)}

/* ── HEADER ── */
.nav{position:sticky;top:0;z-index:200;display:flex;align-items:center;padding:0 40px;
  height:48px;background:rgba(3,8,14,0.96);border-bottom:1px solid var(--border);
  backdrop-filter:blur(16px);gap:16px}
.nav-logo{font-family:var(--ff-display);font-size:14px;font-weight:900;color:var(--green);
  letter-spacing:2px;cursor:pointer}
.nav-logo span{color:var(--text);opacity:.4}
.nav-tabs{display:flex;gap:2px;margin-left:24px}
.ntab{padding:6px 15px;font-size:10px;letter-spacing:2px;text-transform:uppercase;
  color:var(--muted2);cursor:pointer;border:none;background:transparent;
  font-family:var(--ff-mono);border-bottom:2px solid transparent;transition:all .2s}
.ntab.active{color:var(--green);border-bottom-color:var(--green)}
.ntab:hover:not(.active){color:var(--text)}

/* ── FOOTER ── */
.site-footer{border-top:1px solid var(--border);padding:24px 40px;
  display:flex;align-items:center;justify-content:space-between;gap:16px;
  background:rgba(3,8,14,0.9)}
.footer-left{display:flex;align-items:center;gap:28px}
.footer-logo{font-family:var(--ff-display);font-size:12px;font-weight:900;
  color:var(--green);letter-spacing:2px;cursor:pointer}
.footer-logo span{color:var(--text);opacity:.35}
.footer-links{display:flex;gap:16px}
.footer-link{font-size:9px;letter-spacing:2px;text-transform:uppercase;
  color:var(--muted2);cursor:pointer;transition:color .2s;background:none;
  border:none;font-family:var(--ff-mono)}
.footer-link:hover{color:var(--green)}
.footer-right{text-align:right}
.footer-copy{font-size:9px;color:var(--muted);letter-spacing:1px}
.footer-disc{font-size:8px;color:var(--muted);opacity:.55;margin-top:3px;letter-spacing:.5px}

/* ── BG ELEMENTS ── */
.bg-grid{position:fixed;inset:0;pointer-events:none;z-index:0;
  background-image:linear-gradient(rgba(0,220,130,0.03) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,220,130,0.03) 1px,transparent 1px);
  background-size:48px 48px}
.bg-glow{position:fixed;left:-160px;top:25%;width:500px;height:500px;border-radius:50%;
  background:radial-gradient(circle,rgba(0,220,130,0.055) 0%,transparent 70%);
  pointer-events:none;z-index:0}
.bg-glow2{position:fixed;right:-160px;bottom:15%;width:420px;height:420px;border-radius:50%;
  background:radial-gradient(circle,rgba(0,119,255,0.07) 0%,transparent 70%);
  pointer-events:none;z-index:0}

/* ── SHARED UTILS ── */
.eyebrow{font-size:9px;letter-spacing:4px;text-transform:uppercase;color:var(--green);
  margin-bottom:20px;display:flex;align-items:center;gap:10px}
.eyebrow::before{content:'';display:block;width:32px;height:1px;background:var(--border2)}
.section-label{font-size:9px;color:var(--green);letter-spacing:4px;text-transform:uppercase;margin-bottom:20px}
.live-dot{width:6px;height:6px;border-radius:50%;background:var(--green);
  animation:pulse 1.4s ease infinite;display:inline-block;margin-right:6px}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}

/* ═══════════════════════════════════════════════════
   HOME PAGE
═══════════════════════════════════════════════════ */
.home-wrap{position:relative;z-index:1}
.hero{padding:80px 40px 60px;border-bottom:1px solid var(--border);position:relative;overflow:hidden}
.hero-glow-r{position:absolute;right:-160px;top:-100px;width:600px;height:600px;border-radius:50%;
  background:radial-gradient(circle,rgba(0,119,255,0.09) 0%,transparent 70%);pointer-events:none}
.hero-title{font-family:var(--ff-display);font-size:clamp(44px,7vw,90px);font-weight:900;
  line-height:.95;letter-spacing:-3px;margin-bottom:28px;max-width:860px}
.hero-title .accent{color:var(--green)}
.hero-title .dim{color:rgba(232,244,240,0.18)}
.hero-desc{font-size:13px;line-height:1.8;color:rgba(200,232,216,.6);max-width:500px;margin-bottom:40px}
.hero-actions{display:flex;gap:12px;flex-wrap:wrap}
.btn-primary{padding:14px 32px;background:var(--green);color:#03080e;border:none;
  font-family:var(--ff-display);font-size:10px;font-weight:700;letter-spacing:2px;
  text-transform:uppercase;cursor:pointer;transition:all .2s}
.btn-primary:hover{background:#00ffaa;box-shadow:0 0 24px rgba(0,220,130,.4)}
.btn-ghost{padding:14px 32px;background:transparent;border:1px solid var(--border2);
  color:var(--muted2);font-family:var(--ff-mono);font-size:11px;letter-spacing:1px;
  cursor:pointer;transition:all .2s}
.btn-ghost:hover{border-color:var(--green);color:var(--green)}
.stat-bar{display:flex;border-top:1px solid var(--border);margin-top:60px;padding-top:36px;gap:0}
.stat-item{flex:1;padding-right:32px;border-right:1px solid var(--border);margin-right:32px}
.stat-item:last-child{border-right:none;margin-right:0}
.stat-num{font-family:var(--ff-display);font-size:36px;font-weight:900;color:var(--green);line-height:1;letter-spacing:-2px}
.stat-lbl{font-size:9px;color:var(--muted);letter-spacing:2px;text-transform:uppercase;margin-top:6px}
.ticker-wrap{background:var(--panel);border-bottom:1px solid var(--border);overflow:hidden;padding:12px 0}
.ticker-track{display:flex;gap:56px;animation:ticker 28s linear infinite;width:max-content}
@keyframes ticker{from{transform:translateX(0)}to{transform:translateX(-50%)}}
.ticker-item{display:flex;align-items:center;gap:10px;white-space:nowrap;font-size:11px;font-weight:700}
.t-sym{color:var(--muted)}.t-val{color:var(--text)}.t-up{color:var(--green)}.t-dn{color:var(--red)}
.t-sep{width:5px;height:5px;border-radius:50%;background:var(--border)}
.home-section{padding:80px 40px}
.home-title{font-family:var(--ff-display);font-size:clamp(28px,3.5vw,44px);font-weight:900;
  letter-spacing:-1.5px;line-height:1;margin-bottom:48px}
.acc-grid{display:grid;grid-template-columns:1fr 1fr;gap:2px;background:var(--border)}
.acc-card{background:var(--panel);padding:40px;transition:background .25s}
.acc-card:hover{background:var(--panel2)}
.acc-num{font-family:var(--ff-display);font-size:56px;font-weight:900;color:var(--green);
  line-height:1;letter-spacing:-3px}
.acc-title{font-family:var(--ff-display);font-size:17px;font-weight:700;margin:12px 0 8px}
.acc-desc{font-size:12px;line-height:1.7;color:rgba(200,232,216,.5)}
.cases-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:2px;background:var(--border)}
.case-card{background:var(--panel);padding:32px;transition:background .25s;position:relative;overflow:hidden}
.case-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;
  background:var(--green);transform:scaleX(0);transform-origin:left;transition:transform .35s}
.case-card:hover::before{transform:scaleX(1)}
.case-card:hover{background:var(--panel2)}
.case-tag{display:inline-block;font-size:8px;letter-spacing:2px;text-transform:uppercase;
  color:var(--green);border:1px solid rgba(0,220,130,.25);padding:3px 8px;margin-bottom:14px}
.case-asset{font-family:var(--ff-display);font-size:28px;font-weight:900;letter-spacing:-1px;margin-bottom:16px}
.case-metrics{display:flex;gap:20px;margin-bottom:14px}
.case-val{font-family:var(--ff-display);font-size:20px;font-weight:700}
.case-val.pred{color:var(--blue)}.case-val.actual{color:var(--green)}
.case-lbl{font-size:8px;color:var(--muted);letter-spacing:1px;text-transform:uppercase;margin-top:3px}
.case-desc{font-size:11px;line-height:1.7;color:rgba(200,232,216,.45)}
.testi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:2px;background:var(--border)}
.testi-card{background:var(--panel);padding:24px;transition:background .2s}
.testi-card:hover{background:var(--panel2)}
.testi-stars{color:var(--green);font-size:9px;letter-spacing:2px;margin-bottom:10px}
.testi-quote{font-size:11px;line-height:1.75;color:rgba(200,232,216,.55);font-style:italic;margin-bottom:12px}
.testi-name{font-family:var(--ff-display);font-size:12px;font-weight:700}
.testi-role{font-size:9px;color:var(--muted);letter-spacing:1px;text-transform:uppercase;margin-top:3px}
.home-cta{margin:0 40px 80px;background:var(--panel);border:1px solid var(--border);
  padding:60px;display:flex;align-items:center;justify-content:space-between;gap:40px;
  position:relative;overflow:hidden}
.home-cta::after{content:'';position:absolute;right:-80px;top:-80px;width:360px;height:360px;
  border-radius:50%;background:radial-gradient(circle,rgba(0,119,255,0.1) 0%,transparent 70%);pointer-events:none}
.cta-title{font-family:var(--ff-display);font-size:36px;font-weight:900;letter-spacing:-1.5px;max-width:440px}
.cta-title .accent{color:var(--green)}

/* ═══════════════════════════════════════════════════
   FORECASTS (DASHBOARD)
═══════════════════════════════════════════════════ */
.dash-wrap{display:grid;grid-template-columns:1fr 280px;height:calc(100vh - 48px);overflow:hidden;position:relative;z-index:1}
.dash-left{display:grid;grid-template-rows:1fr 200px;overflow:hidden;border-right:1px solid var(--border)}
.dash-right{display:flex;flex-direction:column;overflow:hidden}
.chart-wrap{position:relative;overflow:hidden;background:var(--panel)}
.chart-header{display:flex;align-items:center;gap:10px;padding:10px 14px;border-bottom:1px solid var(--border)}
.ch-title{font-family:var(--ff-display);font-size:11px;font-weight:700;color:var(--green);letter-spacing:2px}
.ch-lbl{font-size:10px;color:var(--muted);letter-spacing:1px}
.chart-canvas{width:100%;height:calc(100% - 40px);display:block}
.pred-section{border-top:1px solid var(--border);background:var(--panel);display:grid;grid-template-columns:1fr 1fr}
.pred-half{padding:12px 16px;display:flex;flex-direction:column;gap:8px}
.pred-half:first-child{border-right:1px solid var(--border)}
.pred-half-title{font-size:9px;letter-spacing:3px;text-transform:uppercase;color:var(--muted)}
.pred-bar-wrap{background:rgba(255,255,255,0.04);height:7px;width:100%}
.pred-bar-fill{height:100%;transition:width .8s ease}
.pred-pct{font-family:var(--ff-display);font-size:26px;font-weight:900;line-height:1}
.pred-pct.up{color:var(--green)}.pred-pct.dn{color:var(--red)}
.pred-meta{display:flex;gap:16px}
.pred-meta-val{font-family:var(--ff-display);font-size:12px;font-weight:700}
.pred-meta-lbl{font-size:8px;color:var(--muted);letter-spacing:1px;text-transform:uppercase;margin-top:2px}
.section-hdr{display:flex;align-items:center;gap:8px;padding:8px 14px;
  border-bottom:1px solid var(--border);background:var(--panel2)}
.section-hdr-title{font-size:9px;letter-spacing:3px;text-transform:uppercase;
  color:var(--green);font-family:var(--ff-display)}
.signal-wrap{padding:10px 14px;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:12px}
.signal-ring{width:50px;height:50px;border-radius:50%;border:2px solid;
  display:flex;align-items:center;justify-content:center;
  font-family:var(--ff-display);font-size:8px;font-weight:700;letter-spacing:1px;flex-shrink:0}
.signal-ring.up{border-color:var(--green);color:var(--green);box-shadow:0 0 12px rgba(0,220,130,.25)}
.signal-ring.dn{border-color:var(--red);color:var(--red);box-shadow:0 0 12px rgba(255,61,90,.25)}
.signal-ring.neutral{border-color:var(--muted);color:var(--muted)}
.signal-main{font-family:var(--ff-display);font-size:11px;font-weight:700;margin-bottom:3px}
.signal-sub{font-size:9px;color:var(--muted);line-height:1.5}
.model-table{width:100%;border-collapse:collapse}
.model-table th{font-size:8px;letter-spacing:2px;text-transform:uppercase;color:var(--muted);
  padding:5px 8px;text-align:right;font-weight:400}
.model-table th:first-child{text-align:left}
.model-table td{padding:4px 8px;font-size:10px;text-align:right;border-top:1px solid rgba(0,220,130,0.05)}
.model-table td:first-child{text-align:left;font-family:var(--ff-display);font-size:9px;
  font-weight:700;color:var(--green);letter-spacing:1px}
.model-row:hover{background:var(--green-dim)}
.acc-pill{display:inline-block;padding:2px 6px;font-size:9px;font-weight:700;font-family:var(--ff-display)}
.acc-pill.hi{background:var(--green-dim);color:var(--green)}
.acc-pill.mid{background:rgba(0,170,255,0.15);color:var(--blue)}
.acc-pill.lo{background:rgba(255,61,90,0.15);color:var(--red)}
.trade-current{padding:12px 14px;border-bottom:1px solid var(--border);background:var(--panel)}
.trade-kv{display:flex;justify-content:space-between;margin-bottom:4px}
.trade-k{font-size:9px;color:var(--muted);text-transform:uppercase;letter-spacing:1px}
.trade-v{font-family:var(--ff-display);font-size:11px;font-weight:700}
.trade-v.up{color:var(--green)}.trade-v.dn{color:var(--red)}
.trade-btn-row{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-top:8px}
.trade-btn{padding:8px 0;border:none;font-family:var(--ff-display);font-size:10px;font-weight:700;
  letter-spacing:2px;cursor:pointer;transition:all .2s;text-transform:uppercase}
.trade-btn.buy{background:var(--green);color:#03080e}
.trade-btn.sell{background:var(--red);color:#fff}
.trade-btn:disabled{opacity:.3;cursor:not-allowed}
.history-section{flex:1;overflow:hidden;display:flex;flex-direction:column}
.history-scroll{flex:1;overflow-y:auto}
.htable{width:100%;border-collapse:collapse}
.htable th{font-size:8px;letter-spacing:1px;text-transform:uppercase;color:var(--muted);
  padding:5px 8px;text-align:left;font-weight:400;position:sticky;top:0;background:var(--panel2)}
.htable td{padding:4px 8px;font-size:10px;border-top:1px solid rgba(0,220,130,.05)}
.htable td.profit.up{color:var(--green);font-weight:600}
.htable td.profit.dn{color:var(--red);font-weight:600}
.side-buy{color:var(--green)}.side-sell{color:var(--red)}
.dash-price{font-family:var(--ff-display);font-size:16px;font-weight:900}
.dash-price.up{color:var(--green)}.dash-price.dn{color:var(--red)}
.price-chg{font-size:10px;padding:2px 7px;font-weight:600}
.price-chg.up{background:var(--green-dim2);color:var(--green)}
.price-chg.dn{background:rgba(255,61,90,.18);color:var(--red)}
.trade-section{flex:1;overflow:hidden;display:flex;flex-direction:column}
.model-section{border-bottom:1px solid var(--border);overflow:hidden}

/* ═══════════════════════════════════════════════════
   LEARN PAGE
═══════════════════════════════════════════════════ */
.learn-wrap{position:relative;z-index:1;max-width:1100px;margin:0 auto;padding:64px 40px 80px}
.learn-hero{padding-bottom:48px;border-bottom:1px solid var(--border);margin-bottom:0}
.learn-title{font-family:var(--ff-display);font-size:clamp(28px,4vw,52px);font-weight:900;
  letter-spacing:-1.5px;line-height:1;margin-bottom:16px}
.learn-sub{font-size:12px;color:var(--muted2);line-height:1.8;max-width:520px;margin-bottom:28px}
.filter-bar{display:flex;gap:8px;flex-wrap:wrap}
.filter-btn{padding:6px 16px;font-size:9px;letter-spacing:2px;text-transform:uppercase;
  border:1px solid var(--border);color:var(--muted2);background:transparent;
  font-family:var(--ff-mono);cursor:pointer;transition:all .2s}
.filter-btn.active{border-color:var(--green);color:var(--green);background:var(--green-dim)}
.filter-btn:hover:not(.active){border-color:var(--border2);color:var(--text)}
.count-bar{padding:14px 0;display:flex;align-items:center;gap:10px;border-bottom:1px solid var(--border);margin-bottom:0}
.count-num{font-family:var(--ff-display);font-size:11px;font-weight:700;color:var(--green)}
.count-lbl{font-size:9px;color:var(--muted);letter-spacing:2px;text-transform:uppercase}
.books-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:2px;background:var(--border)}
.book-card{background:var(--panel);display:flex;flex-direction:column;
  position:relative;overflow:hidden;transition:background .25s;cursor:pointer}
.book-card:hover{background:var(--panel2)}
.book-card::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;
  background:linear-gradient(90deg,transparent,var(--green),transparent);
  transform:scaleX(0);transform-origin:center;transition:transform .4s}
.book-card:hover::before{transform:scaleX(1)}
.book-spine{height:3px;width:100%;flex-shrink:0}
.book-inner{padding:24px 24px 16px;flex:1;display:flex;flex-direction:column;gap:12px}
.book-meta{display:flex;align-items:center;gap:8px}
.book-cat{font-size:8px;letter-spacing:2px;text-transform:uppercase;padding:3px 8px;border:1px solid;font-family:var(--ff-mono)}
.book-num{font-family:var(--ff-display);font-size:9px;font-weight:700;color:var(--muted);margin-left:auto}
.book-title{font-family:var(--ff-display);font-size:13px;font-weight:700;letter-spacing:-.3px;line-height:1.3}
.book-author{font-size:10px;color:var(--muted2);letter-spacing:1px}
.book-desc{font-size:11px;line-height:1.75;color:rgba(200,232,216,.5);
  display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.book-footer{padding:12px 24px 18px;display:flex;align-items:center;gap:12px;border-top:1px solid var(--border)}
.book-read-btn{padding:6px 16px;background:var(--green-dim2);border:1px solid rgba(0,220,130,.3);
  color:var(--green);font-family:var(--ff-mono);font-size:9px;letter-spacing:2px;
  text-transform:uppercase;cursor:pointer;transition:all .2s}
.book-read-btn:hover{background:var(--green);color:#03080e}
.book-level{font-size:9px;color:var(--muted);letter-spacing:1px;text-transform:uppercase;
  margin-left:auto;display:flex;align-items:center;gap:6px}
.level-dot{width:5px;height:5px;border-radius:50%}
.modal-overlay{position:fixed;inset:0;z-index:300;background:rgba(3,8,14,.9);
  backdrop-filter:blur(10px);display:flex;align-items:center;justify-content:center;padding:24px}
.modal{background:var(--panel);border:1px solid var(--border2);max-width:580px;width:100%;
  max-height:80vh;display:flex;flex-direction:column;overflow:hidden;animation:modalIn .22s ease}
@keyframes modalIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
.modal-spine{height:3px;flex-shrink:0}
.modal-header{padding:24px 24px 0;display:flex;align-items:start;gap:12px}
.modal-close{margin-left:auto;width:26px;height:26px;border:1px solid var(--border2);
  background:transparent;color:var(--muted2);font-size:12px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;flex-shrink:0;font-family:var(--ff-mono);transition:all .2s}
.modal-close:hover{border-color:var(--green);color:var(--green)}
.modal-body{padding:16px 24px 24px;overflow-y:auto;flex:1}
.modal-title{font-family:var(--ff-display);font-size:18px;font-weight:900;letter-spacing:-.5px;line-height:1.2;margin-bottom:6px}
.modal-author{font-size:10px;color:var(--muted2);letter-spacing:2px;text-transform:uppercase;margin-bottom:16px}
.modal-desc{font-size:12px;line-height:1.85;color:rgba(200,232,216,.65)}
.modal-tags{display:flex;gap:6px;flex-wrap:wrap;margin-top:16px}
.modal-tag{font-size:8px;letter-spacing:2px;text-transform:uppercase;padding:3px 8px;border:1px solid var(--border);color:var(--muted2)}

/* ═══════════════════════════════════════════════════
   API PAGE
═══════════════════════════════════════════════════ */
.api-wrap{position:relative;z-index:1;min-height:calc(100vh - 48px - 73px);
  display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 24px}
.lock-wrap{width:80px;height:80px;border:1px solid var(--border2);
  display:flex;align-items:center;justify-content:center;position:relative;
  margin-bottom:32px;animation:lockPulse 3s ease infinite}
.lock-wrap::before{content:'';position:absolute;inset:-6px;border:1px solid rgba(0,220,130,0.07)}
.lock-wrap::after{content:'';position:absolute;inset:-12px;border:1px solid rgba(0,220,130,0.04)}
@keyframes lockPulse{0%,100%{box-shadow:0 0 0 0 rgba(0,220,130,0)}50%{box-shadow:0 0 24px 4px rgba(0,220,130,0.12)}}
.lock-icon{font-size:32px;filter:drop-shadow(0 0 10px rgba(0,220,130,0.4))}
.api-headline{font-family:var(--ff-display);font-size:clamp(24px,3.5vw,44px);font-weight:900;
  letter-spacing:-1px;line-height:1;text-align:center;margin-bottom:14px}
.api-headline .dim{color:rgba(200,232,216,.18)}
.api-subtext{font-size:12px;color:var(--muted2);line-height:1.8;text-align:center;max-width:440px;margin-bottom:36px}
.feat-pills{display:flex;flex-wrap:wrap;gap:7px;justify-content:center;margin-bottom:40px}
.feat-pill{display:flex;align-items:center;gap:7px;padding:5px 12px;border:1px solid var(--border);
  font-size:9px;letter-spacing:1px;text-transform:uppercase;color:var(--muted2)}
.feat-dot{width:5px;height:5px;border-radius:50%;background:var(--green);flex-shrink:0}
.terminal{width:100%;max-width:500px;background:var(--panel);border:1px solid var(--border);margin-bottom:40px}
.terminal-bar{display:flex;align-items:center;gap:7px;padding:9px 12px;
  border-bottom:1px solid var(--border);background:var(--panel2)}
.t-dot{width:7px;height:7px;border-radius:50%}
.t-title{font-size:9px;letter-spacing:2px;text-transform:uppercase;color:var(--muted);margin-left:4px}
.terminal-body{padding:16px 18px;font-size:11px;line-height:2;color:var(--muted2)}
.t-green{color:var(--green)}.t-blue{color:var(--blue)}
.t-locked{color:var(--red);display:flex;align-items:center;gap:8px;
  padding:9px 12px;margin-top:4px;background:rgba(255,61,90,0.06);
  border:1px solid rgba(255,61,90,.2);font-size:10px;letter-spacing:1px}
.api-cta{padding:13px 36px;background:var(--green);color:#03080e;border:none;
  font-family:var(--ff-display);font-size:10px;font-weight:700;letter-spacing:2px;
  text-transform:uppercase;cursor:pointer;transition:all .25s;position:relative;overflow:hidden}
.api-cta:hover{background:#00ffaa;box-shadow:0 0 28px rgba(0,220,130,.4)}
.api-modal-overlay{position:fixed;inset:0;z-index:400;background:rgba(3,8,14,.9);
  backdrop-filter:blur(10px);display:flex;align-items:center;justify-content:center;padding:24px;
  animation:fadeIn .2s ease}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
.api-modal{background:var(--panel);border:1px solid var(--border2);width:100%;max-width:400px;
  position:relative;overflow:hidden;animation:slideUp .22s ease}
@keyframes slideUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
.api-modal-accent{height:2px;background:linear-gradient(90deg,transparent,var(--green),transparent)}
.api-modal-inner{padding:32px 28px 28px}
.api-modal-icon{width:48px;height:48px;border:1px solid var(--border2);
  display:flex;align-items:center;justify-content:center;font-size:20px;margin-bottom:18px}
.api-modal-title{font-family:var(--ff-display);font-size:15px;font-weight:900;
  letter-spacing:-.3px;line-height:1.3;margin-bottom:10px}
.api-modal-title .accent{color:var(--green)}
.api-modal-desc{font-size:11px;color:var(--muted2);line-height:1.8;margin-bottom:24px}
.contact-row{display:flex;align-items:center;gap:12px;padding:11px 13px;
  border:1px solid var(--border);margin-bottom:8px;transition:border-color .2s;cursor:pointer}
.contact-row:hover{border-color:var(--border2)}
.contact-icon{width:30px;height:30px;border:1px solid var(--border);
  display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0}
.contact-label{font-size:8px;letter-spacing:2px;text-transform:uppercase;color:var(--muted);margin-bottom:3px}
.contact-val{font-size:11px;color:var(--text)}
.api-ok-btn{width:100%;margin-top:18px;padding:11px;background:var(--green-dim2);
  border:1px solid rgba(0,220,130,.3);color:var(--green);font-family:var(--ff-display);
  font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;cursor:pointer;transition:all .2s}
.api-ok-btn:hover{background:var(--green);color:#03080e}
.modal-close-x{position:absolute;top:12px;right:12px;width:24px;height:24px;
  border:1px solid var(--border);background:transparent;color:var(--muted2);font-size:11px;
  cursor:pointer;display:flex;align-items:center;justify-content:center;
  font-family:var(--ff-mono);transition:all .2s;z-index:2}
.modal-close-x:hover{border-color:var(--green);color:var(--green)}

/* ═══════════════════════════════════════════════════
   GUIDE PAGE
═══════════════════════════════════════════════════ */
.guide-wrap{position:relative;z-index:1;max-width:980px;margin:0 auto;padding:64px 40px 100px}
.guide-title{font-family:var(--ff-display);font-size:clamp(28px,4vw,50px);font-weight:900;
  letter-spacing:-1.5px;line-height:1;margin-bottom:14px}
.guide-sub{font-size:12px;color:var(--muted2);line-height:1.8;max-width:520px;margin-bottom:48px}
.progress-row{display:flex;align-items:center;margin-bottom:56px;overflow-x:auto;padding-bottom:4px}
.prog-step{display:flex;align-items:center;flex-shrink:0;cursor:pointer}
.prog-node{width:30px;height:30px;border:1px solid var(--border2);
  display:flex;align-items:center;justify-content:center;font-family:var(--ff-display);
  font-size:9px;font-weight:700;transition:all .25s;flex-shrink:0}
.prog-node.done{background:var(--green);border-color:var(--green);color:#03080e}
.prog-node.active{border-color:var(--green);color:var(--green);box-shadow:0 0 12px rgba(0,220,130,.2)}
.prog-node.idle{color:var(--muted)}
.prog-label{font-size:9px;letter-spacing:1px;text-transform:uppercase;
  padding:0 10px;color:var(--muted2);white-space:nowrap;transition:color .2s}
.prog-label.active{color:var(--green)}
.prog-line{width:36px;height:1px;background:var(--border);flex-shrink:0;transition:background .3s}
.prog-line.done{background:var(--green)}
.steps{display:flex;flex-direction:column;gap:2px;background:var(--border)}
.step-card{background:var(--panel);display:grid;grid-template-columns:72px 1fr;
  transition:background .2s;position:relative;overflow:hidden}
.step-card.active-card{background:var(--panel2)}
.step-card::before{content:'';position:absolute;left:0;top:0;bottom:0;width:2px;
  background:var(--green);transform:scaleY(0);transform-origin:top;transition:transform .35s}
.step-card.active-card::before{transform:scaleY(1)}
.step-num-col{display:flex;flex-direction:column;align-items:center;justify-content:flex-start;
  padding:28px 0;gap:10px;border-right:1px solid var(--border)}
.step-circle{width:40px;height:40px;border:1px solid;display:flex;align-items:center;justify-content:center;
  font-family:var(--ff-display);font-size:13px;font-weight:900;transition:all .25s}
.step-circle.done{border-color:var(--green);background:var(--green);color:#03080e}
.step-circle.active{border-color:var(--green);color:var(--green);box-shadow:0 0 14px rgba(0,220,130,.2)}
.step-circle.idle{border-color:var(--border2);color:var(--muted)}
.step-body{padding:28px 32px}
.step-tag{font-size:8px;letter-spacing:3px;text-transform:uppercase;color:var(--green);margin-bottom:8px}
.step-title{font-family:var(--ff-display);font-size:17px;font-weight:900;letter-spacing:-.5px;margin-bottom:18px}
.info-grid{display:flex;flex-direction:column;gap:8px;margin-bottom:18px}
.info-row{display:flex;align-items:flex-start;gap:12px;padding:11px 14px;
  border:1px solid var(--border);transition:border-color .2s}
.info-row:hover{border-color:var(--border2)}
.info-icon{width:26px;height:26px;border:1px solid var(--border);
  display:flex;align-items:center;justify-content:center;font-size:11px;flex-shrink:0;margin-top:2px}
.info-label{font-size:8px;letter-spacing:2px;text-transform:uppercase;color:var(--muted);margin-bottom:3px}
.info-val{font-size:12px;color:var(--text);line-height:1.6}
.info-val .accent{color:var(--green);font-weight:600}
.info-val .highlight{display:inline-block;background:var(--green-dim2);
  border:1px solid rgba(0,220,130,.3);color:var(--green);padding:2px 9px;
  font-family:var(--ff-display);font-size:11px;font-weight:700;letter-spacing:1px}
.info-val .amt{font-family:var(--ff-display);font-size:18px;font-weight:900;color:var(--green);letter-spacing:-.5px}
.tip-box{display:flex;align-items:flex-start;gap:10px;padding:11px 13px;
  background:rgba(255,199,60,0.06);border:1px solid rgba(255,199,60,0.2);
  font-size:11px;color:rgba(255,199,60,.8);line-height:1.7}
.tip-icon{font-size:13px;flex-shrink:0;margin-top:1px}
.mark-btn{padding:8px 20px;border:none;font-family:var(--ff-display);font-size:9px;
  font-weight:700;letter-spacing:2px;text-transform:uppercase;cursor:pointer;transition:all .25s;margin-top:16px}
.mark-btn.done-btn{background:var(--green-dim2);color:var(--green);border:1px solid rgba(0,220,130,.3)}
.mark-btn.done-btn:hover{background:var(--green);color:#03080e}
.mark-btn.undo-btn{background:transparent;color:var(--muted2);border:1px solid var(--border)}
.mark-btn.undo-btn:hover{border-color:var(--border2);color:var(--text)}
.complete-banner{margin-top:2px;padding:18px 24px;background:var(--green);display:flex;align-items:center;gap:14px}
.complete-text{font-family:var(--ff-display);font-size:13px;font-weight:900;color:#03080e;letter-spacing:-.3px}
.complete-sub{font-size:10px;color:rgba(3,8,14,.55);margin-top:2px;font-family:var(--ff-mono)}
.summary{margin-top:40px;padding:28px;border:1px solid var(--border2);background:var(--green-dim);
  display:grid;grid-template-columns:1fr 1fr;gap:20px;position:relative}
.summary::before{content:'QUICK REFERENCE';position:absolute;top:-8px;left:16px;
  font-size:8px;letter-spacing:3px;color:var(--green);background:var(--bg);padding:0 8px}
.sum-k{font-size:8px;letter-spacing:2px;text-transform:uppercase;color:var(--muted2);margin-bottom:5px}
.sum-v{font-family:var(--ff-display);font-size:13px;font-weight:700;color:var(--green)}
.about-section{margin-top:56px;padding:36px;background:var(--panel);border:1px solid var(--border);position:relative;overflow:hidden}
.about-section::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;
  background:linear-gradient(90deg,transparent,var(--green),transparent)}
.about-label{font-size:8px;letter-spacing:3px;text-transform:uppercase;color:var(--green);margin-bottom:14px}
.about-title{font-family:var(--ff-display);font-size:20px;font-weight:900;letter-spacing:-.5px;margin-bottom:18px}
.about-body{font-size:12px;color:var(--muted2);line-height:1.9;max-width:680px}
.about-body p{margin-bottom:12px}
.about-body p:last-child{margin-bottom:0}
.trader-card{display:flex;align-items:center;gap:18px;padding:18px 22px;
  border:1px solid var(--border2);background:var(--panel2);margin-bottom:20px}
.trader-avatar{width:48px;height:48px;border:1px solid var(--border2);
  display:flex;align-items:center;justify-content:center;font-size:20px;
  flex-shrink:0;background:var(--green-dim)}
.trader-name{font-family:var(--ff-display);font-size:15px;font-weight:900;color:var(--green);letter-spacing:-.3px}
.trader-sub{font-size:9px;color:var(--muted2);letter-spacing:2px;text-transform:uppercase;margin-top:3px}
.trader-stats{display:flex;gap:22px;margin-left:auto}
.tstat-val{font-family:var(--ff-display);font-size:14px;font-weight:700;color:var(--text)}
.tstat-lbl{font-size:8px;color:var(--muted);letter-spacing:1px;text-transform:uppercase;margin-top:2px}
.clock-strip{display:flex;align-items:center;gap:14px;padding:13px 18px;
  border:1px solid rgba(0,119,255,0.2);background:rgba(0,119,255,0.05);margin-bottom:18px}
.clock-time{font-family:var(--ff-display);font-size:17px;font-weight:900;color:var(--blue);letter-spacing:1px}
.clock-text{font-size:11px;color:var(--blue);line-height:1.6}

/* ═══════════════════════════════════════════════════
   CONTACT PAGE
═══════════════════════════════════════════════════ */
.contact-wrap{position:relative;z-index:1;max-width:1000px;margin:0 auto;padding:64px 40px 100px}
.contact-hero{padding-bottom:48px;border-bottom:1px solid var(--border);margin-bottom:56px}
.contact-title{font-family:var(--ff-display);font-size:clamp(28px,4vw,52px);font-weight:900;
  letter-spacing:-1.5px;line-height:1;margin-bottom:16px}
.contact-sub{font-size:12px;color:var(--muted2);line-height:1.9;max-width:560px}

.contact-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:2px;background:var(--border);margin-bottom:2px}
.channel-card{
  background:var(--panel);padding:36px 32px;
  display:flex;flex-direction:column;gap:20px;
  position:relative;overflow:hidden;
  text-decoration:none;color:inherit;
  transition:background .25s;cursor:pointer;
}
.channel-card:hover{background:var(--panel2)}
.channel-card::before{
  content:'';position:absolute;top:0;left:0;right:0;height:2px;
  transform:scaleX(0);transform-origin:left;transition:transform .4s;
}
.channel-card:hover::before{transform:scaleX(1)}
.channel-card.telegram::before{background:#2AABEE}
.channel-card.discord::before{background:#5865F2}
.channel-card.facebook::before{background:#1877F2}

.channel-icon-wrap{
  width:56px;height:56px;border:1px solid var(--border2);
  display:flex;align-items:center;justify-content:center;font-size:26px;
  position:relative;
}
.channel-icon-wrap::after{
  content:'';position:absolute;inset:-1px;
  opacity:0;transition:opacity .3s;
}
.channel-card:hover .channel-icon-wrap::after{opacity:1}
.channel-name{font-family:var(--ff-display);font-size:16px;font-weight:900;letter-spacing:-.3px}
.channel-handle{font-size:10px;letter-spacing:1px;margin-top:4px}
.channel-desc{font-size:11px;line-height:1.75;color:rgba(200,232,216,.5)}
.channel-cta{
  display:inline-flex;align-items:center;gap:8px;
  padding:8px 18px;font-size:9px;letter-spacing:2px;text-transform:uppercase;
  font-family:var(--ff-mono);border:1px solid;cursor:pointer;background:transparent;
  transition:all .2s;width:fit-content;margin-top:auto;
}
.channel-card.telegram .channel-name{color:#2AABEE}
.channel-card.telegram .channel-handle{color:rgba(42,171,238,.6)}
.channel-card.telegram .channel-cta{border-color:rgba(42,171,238,.3);color:#2AABEE}
.channel-card.telegram:hover .channel-cta{background:#2AABEE;color:#03080e;border-color:#2AABEE}
.channel-card.telegram .channel-icon-wrap{border-color:rgba(42,171,238,.3)}

.channel-card.discord .channel-name{color:#5865F2}
.channel-card.discord .channel-handle{color:rgba(88,101,242,.6)}
.channel-card.discord .channel-cta{border-color:rgba(88,101,242,.3);color:#5865F2}
.channel-card.discord:hover .channel-cta{background:#5865F2;color:#fff;border-color:#5865F2}
.channel-card.discord .channel-icon-wrap{border-color:rgba(88,101,242,.3)}

.channel-card.facebook .channel-name{color:#1877F2}
.channel-card.facebook .channel-handle{color:rgba(24,119,242,.6)}
.channel-card.facebook .channel-cta{border-color:rgba(24,119,242,.3);color:#1877F2}
.channel-card.facebook:hover .channel-cta{background:#1877F2;color:#fff;border-color:#1877F2}
.channel-card.facebook .channel-icon-wrap{border-color:rgba(24,119,242,.3)}

.contact-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:2px;background:var(--border);margin-bottom:56px}
.cstat-card{background:var(--panel);padding:28px 24px;text-align:center;transition:background .2s}
.cstat-card:hover{background:var(--panel2)}
.cstat-num{font-family:var(--ff-display);font-size:32px;font-weight:900;color:var(--green);
  line-height:1;letter-spacing:-1.5px;margin-bottom:8px}
.cstat-lbl{font-size:9px;color:var(--muted);letter-spacing:2px;text-transform:uppercase}

.faq-section{margin-top:56px}
.faq-title{font-family:var(--ff-display);font-size:22px;font-weight:900;letter-spacing:-.5px;margin-bottom:24px}
.faq-list{display:flex;flex-direction:column;gap:2px;background:var(--border)}
.faq-item{background:var(--panel);overflow:hidden}
.faq-q{
  display:flex;align-items:center;justify-content:space-between;
  padding:18px 24px;cursor:pointer;transition:background .2s;gap:16px;
}
.faq-q:hover{background:var(--panel2)}
.faq-q-text{font-family:var(--ff-display);font-size:12px;font-weight:700;letter-spacing:-.2px}
.faq-chevron{font-size:10px;color:var(--green);flex-shrink:0;transition:transform .25s;font-family:var(--ff-mono)}
.faq-chevron.open{transform:rotate(180deg)}
.faq-a{padding:0 24px 18px;font-size:12px;line-height:1.8;color:rgba(200,232,216,.55)}

.email-strip{
  display:flex;align-items:center;justify-content:space-between;gap:24px;
  padding:32px 36px;background:var(--panel);border:1px solid var(--border);
  margin-top:56px;position:relative;overflow:hidden;
}
.email-strip::before{
  content:'';position:absolute;top:0;left:0;right:0;height:1px;
  background:linear-gradient(90deg,transparent,var(--green),transparent);
}
.email-strip-left{}
.email-strip-label{font-size:8px;letter-spacing:3px;text-transform:uppercase;color:var(--green);margin-bottom:8px}
.email-strip-title{font-family:var(--ff-display);font-size:18px;font-weight:900;letter-spacing:-.3px}
.email-addr{
  font-family:var(--ff-display);font-size:14px;font-weight:700;
  color:var(--green);letter-spacing:.5px;margin-top:6px;
}
.email-btn{
  padding:12px 28px;background:var(--green);color:#03080e;border:none;
  font-family:var(--ff-display);font-size:10px;font-weight:700;
  letter-spacing:2px;text-transform:uppercase;cursor:pointer;
  transition:all .2s;flex-shrink:0;
}
.email-btn:hover{background:#00ffaa;box-shadow:0 0 24px rgba(0,220,130,.4)}

/* ═══════════════════════════════════════════════════
   HAMBURGER BUTTON
═══════════════════════════════════════════════════ */
.hamburger{
  display:none;align-items:center;justify-content:center;
  width:38px;height:38px;cursor:pointer;
  background:var(--panel);border:1px solid var(--border2);
  margin-left:auto;flex-shrink:0;position:relative;z-index:201;
  transition:border-color .2s;
}
.hamburger:hover{border-color:var(--green)}
.ham-box{width:18px;height:12px;position:relative;display:flex;flex-direction:column;justify-content:space-between}
.ham-line{
  display:block;width:100%;height:1.5px;background:var(--green);
  transition:all .35s cubic-bezier(.4,0,.2,1);transform-origin:center;border-radius:1px;
}
.hamburger.open .ham-line:nth-child(1){transform:translateY(5.25px) rotate(45deg)}
.hamburger.open .ham-line:nth-child(2){opacity:0;transform:scaleX(0)}
.hamburger.open .ham-line:nth-child(3){transform:translateY(-5.25px) rotate(-45deg)}

/* ═══════════════════════════════════════════════════
   MOBILE MENU OVERLAY (FULL SCREEN)
═══════════════════════════════════════════════════ */
.mobile-menu-overlay{
  position:fixed;inset:0;z-index:198;
  background:rgba(3,8,14,0);pointer-events:none;
  transition:background .3s ease;
}
.mobile-menu-overlay.open{background:rgba(3,8,14,0.6);pointer-events:all}

.mobile-menu{
  position:fixed;top:0;right:0;bottom:0;z-index:200;
  width:min(320px, 88vw);
  background:var(--panel);
  border-left:1px solid var(--border2);
  display:flex;flex-direction:column;
  transform:translateX(100%);
  transition:transform .35s cubic-bezier(.4,0,.2,1);
  overflow:hidden;
}
.mobile-menu.open{transform:translateX(0)}

/* Menu header bar */
.mob-menu-header{
  display:flex;align-items:center;justify-content:space-between;
  padding:0 20px;height:52px;
  border-bottom:1px solid var(--border);flex-shrink:0;
  background:var(--panel2);
}
.mob-menu-logo{
  font-family:var(--ff-display);font-size:13px;font-weight:900;
  color:var(--green);letter-spacing:2px;
}
.mob-menu-logo span{color:var(--text);opacity:.4}
.mob-close-btn{
  width:30px;height:30px;border:1px solid var(--border2);
  background:transparent;color:var(--muted2);font-size:14px;
  cursor:pointer;display:flex;align-items:center;justify-content:center;
  font-family:var(--ff-mono);transition:all .2s;
}
.mob-close-btn:hover{border-color:var(--green);color:var(--green)}

/* Nav items */
.mob-nav{flex:1;padding:12px 0;overflow-y:auto}
.mob-tab{
  display:flex;align-items:center;gap:16px;
  padding:0 20px;height:58px;
  font-size:10px;letter-spacing:3px;text-transform:uppercase;
  color:var(--muted2);cursor:pointer;border:none;background:transparent;
  font-family:var(--ff-mono);text-align:left;transition:all .2s;
  width:100%;border-left:2px solid transparent;
  position:relative;
}
.mob-tab::after{
  content:'';position:absolute;inset:0;
  background:var(--green);opacity:0;transition:opacity .2s;pointer-events:none;
}
.mob-tab.active{
  color:var(--green);border-left-color:var(--green);
  background:linear-gradient(90deg,var(--green-dim),transparent);
}
.mob-tab:hover:not(.active){color:var(--text);background:rgba(255,255,255,0.03)}
.mob-tab-icon{
  width:32px;height:32px;border:1px solid var(--border);
  display:flex;align-items:center;justify-content:center;
  font-size:14px;flex-shrink:0;transition:border-color .2s;
}
.mob-tab.active .mob-tab-icon{border-color:rgba(0,220,130,.4);background:var(--green-dim)}
.mob-tab-text{flex:1}
.mob-tab-label{font-size:10px;letter-spacing:3px;display:block}
.mob-tab-desc{font-size:9px;color:var(--muted);letter-spacing:.5px;margin-top:2px;
  text-transform:none;letter-spacing:0;display:block;font-family:var(--ff-mono)}
.mob-tab.active .mob-tab-desc{color:var(--muted2)}
.mob-tab-arrow{font-size:10px;color:var(--muted);transition:all .2s;margin-left:auto}
.mob-tab.active .mob-tab-arrow{color:var(--green)}
.mob-tab:hover .mob-tab-arrow{transform:translateX(3px);color:var(--text)}

/* Divider */
.mob-divider{height:1px;background:var(--border);margin:8px 20px}

/* Menu footer */
.mob-menu-footer{
  padding:16px 20px;border-top:1px solid var(--border);
  background:var(--panel2);flex-shrink:0;
}
.mob-footer-label{font-size:8px;letter-spacing:3px;text-transform:uppercase;color:var(--muted);margin-bottom:10px}
.mob-social-row{display:flex;gap:8px}
.mob-social-btn{
  padding:7px 12px;border:1px solid var(--border);background:transparent;
  font-size:9px;letter-spacing:1px;text-transform:uppercase;color:var(--muted2);
  font-family:var(--ff-mono);cursor:pointer;transition:all .2s;
  display:flex;align-items:center;gap:6px;text-decoration:none;
}
.mob-social-btn:hover{border-color:var(--border2);color:var(--text)}

/* ═══════════════════════════════════════════════════
   RESPONSIVE — TABLET (max 1024px)
═══════════════════════════════════════════════════ */
@media(max-width:1024px){
  .nav{padding:0 24px}
  .hero{padding:80px 24px 60px}
  .home-section{padding:60px 24px}
  .home-cta{margin:0 24px 60px;padding:48px 32px}
  .cases-grid{grid-template-columns:1fr}
  .testi-grid{grid-template-columns:1fr 1fr}
  .learn-wrap,.guide-wrap,.contact-wrap{padding:48px 24px 80px}
  .books-grid{grid-template-columns:repeat(auto-fill,minmax(280px,1fr))}
  .contact-grid{grid-template-columns:1fr 1fr}
  .cstat-num{font-size:24px}
  .site-footer{padding:20px 24px}
  .dash-right{width:240px}
}

/* ═══════════════════════════════════════════════════
   RESPONSIVE — MOBILE (max 768px)
═══════════════════════════════════════════════════ */
@media(max-width:768px){
  /* NAV */
  .nav{padding:0 16px;height:52px}
  .nav-tabs{display:none}
  .hamburger{display:flex}
  /* FOOTER */
  .site-footer{flex-direction:column;align-items:flex-start;gap:20px;padding:24px 16px}
  .footer-left{flex-direction:column;align-items:flex-start;gap:12px}
  .footer-links{display:none}
  .footer-right{text-align:left}

  /* HOME HERO */
  .hero{padding:56px 16px 40px}
  .hero-title{font-size:clamp(36px,10vw,60px);letter-spacing:-2px}
  .hero-desc{font-size:12px}
  .hero-actions{flex-direction:column;gap:10px}
  .btn-primary,.btn-ghost{width:100%;text-align:center;padding:14px 20px}
  .stat-bar{flex-direction:column;gap:0;border-top:1px solid var(--border);margin-top:40px;padding-top:0}
  .stat-item{
    flex:none;border-right:none;margin-right:0;
    padding:20px 0;border-bottom:1px solid var(--border);
  }
  .stat-item:last-child{border-bottom:none}
  .stat-num{font-size:28px}

  /* HOME SECTIONS */
  .home-section{padding:48px 16px}
  .home-title{font-size:clamp(24px,7vw,36px);margin-bottom:32px}
  .acc-grid{grid-template-columns:1fr}
  .acc-num{font-size:40px}
  .cases-grid{grid-template-columns:1fr}
  .testi-grid{grid-template-columns:1fr}
  .home-cta{flex-direction:column;align-items:flex-start;margin:0 16px 60px;padding:32px 20px;gap:24px}
  .cta-title{font-size:26px}

  /* FORECASTS DASHBOARD */
  .dash-wrap{
    grid-template-columns:1fr;
    grid-template-rows:auto auto;
    height:auto;min-height:calc(100vh - 52px);
  }
  .dash-left{
    grid-template-rows:300px auto;
    height:auto;
    border-right:none;
    border-bottom:1px solid var(--border);
  }
  .dash-right{
    display:flex;width:100%;
    flex-direction:column;max-height:none;
  }
  .signal-wrap{flex-wrap:wrap}
  .model-section{overflow-x:auto}
  .model-table{min-width:320px}
  .trade-current{padding:10px 12px}
  .history-scroll{max-height:200px}

  /* LEARN */
  .learn-wrap{padding:36px 16px 60px}
  .learn-title{font-size:clamp(24px,8vw,40px)}
  .filter-bar{gap:6px}
  .filter-btn{padding:5px 10px;font-size:8px}
  .books-grid{grid-template-columns:1fr;gap:1px}
  .book-inner{padding:20px 18px 12px}
  .book-footer{padding:10px 18px 14px}
  .modal{max-height:90vh}
  .modal-body{padding:14px 18px 20px}

  /* API */
  .api-wrap{padding:48px 16px}
  .api-headline{font-size:clamp(22px,7vw,36px)}
  .feat-pills{gap:6px}
  .feat-pill{font-size:8px;padding:5px 10px}
  .terminal{max-width:100%}
  .api-modal-inner{padding:24px 20px 20px}

  /* GUIDE */
  .guide-wrap{padding:36px 16px 60px}
  .guide-title{font-size:clamp(24px,8vw,40px)}
  .progress-row{gap:0;margin-bottom:32px}
  .prog-label{display:none}
  .prog-line{width:20px}
  .step-card{grid-template-columns:52px 1fr}
  .step-body{padding:20px 14px}
  .step-title{font-size:14px}
  .summary{grid-template-columns:1fr;padding:20px}
  .trader-stats{display:none}
  .about-section{padding:24px 18px}

  /* CONTACT */
  .contact-wrap{padding:36px 16px 60px}
  .contact-title{font-size:clamp(24px,8vw,40px)}
  .contact-grid{grid-template-columns:1fr}
  .contact-stats{grid-template-columns:1fr 1fr;gap:1px}
  .cstat-card{padding:20px 16px}
  .cstat-num{font-size:24px}
  .channel-card{padding:24px 20px}
  .email-strip{flex-direction:column;align-items:flex-start;padding:24px 20px;gap:20px}
  .email-strip-title{font-size:15px}
  .email-addr{font-size:12px}
  .email-btn{width:100%;text-align:center}
  .faq-q{padding:14px 16px}
  .faq-q-text{font-size:11px}
  .faq-a{padding:0 16px 14px}
}

/* ═══════════════════════════════════════════════════
   RESPONSIVE — SMALL MOBILE (max 400px)
═══════════════════════════════════════════════════ */
@media(max-width:400px){
  .hero-title{font-size:32px;letter-spacing:-1.5px}
  .stat-num{font-size:24px}
  .contact-stats{grid-template-columns:1fr}
  .progress-row{overflow-x:auto}
  .channel-cta{width:100%;text-align:center;justify-content:center}
  .acc-num{font-size:32px}
  .cta-title{font-size:22px}
}
`;

/* ─── SHARED COMPONENTS ──────────────────────────────────────────────────── */
const NAV_TABS = ["HOME", "FORECASTS", "LEARN", "API", "GUIDE", "CONTACT"];

const TAB_META = {
  HOME:      { icon: "🏠", desc: "Platform overview & stats" },
  FORECASTS: { icon: "📈", desc: "Live BTC/USDT AI signals" },
  LEARN:     { icon: "📚", desc: "Trading & AI book library" },
  API:       { icon: "⚡", desc: "Full prediction API access" },
  GUIDE:     { icon: "🚀", desc: "Copy trading setup guide" },
  CONTACT:   { icon: "💬", desc: "Support & community channels" },
};

function Header({ activeTab, onTabChange }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const handleNav = (t) => { onTabChange(t); setMenuOpen(false); };

  return (
    <>
      <nav className="nav">
        <div className="nav-logo" onClick={() => handleNav("HOME")}>FIGURES<span>.AI</span></div>
        <div className="nav-tabs">
          {NAV_TABS.map(t => (
            <button key={t} className={`ntab ${activeTab === t ? "active" : ""}`} onClick={() => handleNav(t)}>{t}</button>
          ))}
        </div>
        <button
          className={`hamburger ${menuOpen ? "open" : ""}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle navigation menu"
        >
          <div className="ham-box">
            <span className="ham-line" />
            <span className="ham-line" />
            <span className="ham-line" />
          </div>
        </button>
      </nav>

      <div
        className={`mobile-menu-overlay ${menuOpen ? "open" : ""}`}
        onClick={() => setMenuOpen(false)}
      />

      <div className={`mobile-menu ${menuOpen ? "open" : ""}`}>
        <div className="mob-menu-header">
          <div className="mob-menu-logo">FIGURES<span>.AI</span></div>
          <button className="mob-close-btn" onClick={() => setMenuOpen(false)}>✕</button>
        </div>

        <div className="mob-nav">
          {NAV_TABS.map((t) => (
            <button key={t} className={`mob-tab ${activeTab === t ? "active" : ""}`} onClick={() => handleNav(t)}>
              <div className="mob-tab-icon">{TAB_META[t].icon}</div>
              <div className="mob-tab-text">
                <span className="mob-tab-label">{t}</span>
                <span className="mob-tab-desc">{TAB_META[t].desc}</span>
              </div>
              <span className="mob-tab-arrow">›</span>
            </button>
          ))}
          <div className="mob-divider" />
        </div>

        <div className="mob-menu-footer">
          <div className="mob-footer-label">{/* Join our community */}</div>
          <div className="mob-social-row">
            <a className="mob-social-btn" href="https://t.me/cryptexapp" target="_blank" rel="noopener noreferrer">✈️ TG</a>
            <a className="mob-social-btn" href="https://discord.gg/XtvubjS2M" target="_blank" rel="noopener noreferrer">🎮 Discord</a>
            <a className="mob-social-btn" href="https://www.facebook.com/share/19snmj27Dq/" target="_blank" rel="noopener noreferrer">📘 FB</a>
          </div>
        </div>
      </div>
    </>
  );
}

function Footer({ onTabChange }) {
  return (
    <footer className="site-footer">
      <div className="footer-left">
        <div className="footer-logo" style={{ cursor: "pointer" }} onClick={() => onTabChange("HOME")}>FIGURES<span>.AI</span></div>
        <div className="footer-links">
          {NAV_TABS.map(t => (
            <button key={t} className="footer-link" onClick={() => onTabChange(t)}>{t}</button>
          ))}
        </div>
      </div>
      <div className="footer-right">
        <div className="footer-copy">© 2024 FIGURES AI · ALL RIGHTS RESERVED</div>
        <div className="footer-disc">NOT FINANCIAL ADVICE · SIMULATED DATA · EDUCATIONAL USE ONLY</div>
      </div>
    </footer>
  );
}

/* ─── HOME PAGE ──────────────────────────────────────────────────────────── */
const TICKER = [
  { sym: "BTC/USD", val: "$67,420", chg: "+5.43%", up: true },
  { sym: "ASML", val: "$903.32", chg: "+4.07%", up: true },
  { sym: "GOOG", val: "$153.64", chg: "+6.84%", up: true },
  { sym: "ETH/USD", val: "$3,812", chg: "+3.21%", up: true },
  { sym: "SPY", val: "$512.40", chg: "-0.82%", up: false },
  { sym: "NVDA", val: "$874.15", chg: "+2.10%", up: true },
  { sym: "GLD", val: "$218.60", chg: "+1.05%", up: true },
  { sym: "EUR/USD", val: "1.0842", chg: "+0.34%", up: true },
];
const CASES = [
  { asset: "ASML", tag: "Equity", period: "Jan 31 – Feb 13, 2024", pred: "+6.76%", actual: "+4.07%", desc: "Bullish trend confirmed via call option flow dominance and positive semiconductor sector sentiment across major news outlets." },
  { asset: "BTC/USD", tag: "Crypto", period: "Jan 29 – Feb 7, 2024", pred: "+4.88%", actual: "+5.43%", desc: "On-chain activity surge and rising sentiment index captured before the rally, enabling early directional positioning." },
  { asset: "GOOG", tag: "Equity", period: "Jan 11 – 25, 2024", pred: "+5.12%", actual: "+6.84%", desc: "Persistently low put-call ratio paired with analyst upgrade sentiment led to a precise upward forecast." },
];
const TESTIMONIALS = [
  { q: "The AI community signals completely changed my crypto strategy — performance improved measurably.", name: "Derek Fisher", role: "Architect" },
  { q: "Consistent directional accuracy gives me an edge no conventional analysis tool could match.", name: "Lily Huang", role: "Marketing Consultant" },
  { q: "As a crypto newcomer, the guided predictions made informed trading genuinely accessible.", name: "Rachel Kim", role: "College Student" },
  { q: "Predictive analytics have improved my gold trading outcomes and deepened my market intuition.", name: "Carlos Ramirez", role: "Freelancer" },
  { q: "Reliable forex predictions during spare hours turned into a consistent secondary income stream.", name: "Mike Davis", role: "Taxi Driver" },
  { q: "I reference Figures AI forecast benchmarks in my cryptocurrency economics lectures.", name: "Dr. Nina Patel", role: "Economics Professor" },
  { q: "Real-time signals cut through noise — my team makes faster, better-informed investment calls.", name: "Elliot Zhao", role: "Tech Entrepreneur" },
  { q: "The accuracy benchmarks are compelling for anyone serious about data-driven market analysis.", name: "Emma Clarkson", role: "Teacher" },
];

function HomePage({ onTabChange }) {
  const tickerItems = [...TICKER, ...TICKER];
  return (
    <div className="home-wrap">
      <div className="hero">
        <div className="hero-glow-r" />
        <div className="eyebrow">AI-Powered Financial Intelligence</div>
        <h1 className="hero-title">Predict<br /><span className="accent">Markets.</span><br /><span className="dim">Not Opinions.</span></h1>
        <p className="hero-desc">Figures AI delivers institutional-grade predictive signals to every trader. Deep learning models trained on millions of data points generate verified short-term price forecasts.</p>
        <div className="hero-actions">
          <button className="btn-primary" onClick={() => onTabChange("FORECASTS")}>View Live Predictions</button>
          <button className="btn-ghost" onClick={() => onTabChange("GUIDE")}>Getting Started →</button>
        </div>
        <div className="stat-bar">
          {[["90%+","Directional Accuracy"],["2M+","Simulated Trades"],["100M+","Data Points"],["20K+","Active Users"]].map(([n,l]) => (
            <div className="stat-item" key={l}><div className="stat-num">{n}</div><div className="stat-lbl">{l}</div></div>
          ))}
        </div>
      </div>
      <div className="ticker-wrap">
        <div className="ticker-track">
          {tickerItems.map((t, i) => (
            <div className="ticker-item" key={i}>
              <span className="t-sym">{t.sym}</span>
              <span className="t-val">{t.val}</span>
              <span className={t.up ? "t-up" : "t-dn"}>{t.chg}</span>
              <div className="t-sep" />
            </div>
          ))}
        </div>
      </div>
      <div className="home-section">
        <div className="section-label">{/* Performance */}</div>
        <h2 className="home-title">Accuracy You<br />Can Measure</h2>
        <div className="acc-grid">
          <div className="acc-card"><div className="acc-num">90%+</div><div className="acc-title">Short-Term Price Trend Accuracy</div><div className="acc-desc">Validated against live market closes across equities, crypto, and forex. Tracked continuously — no cherry-picked windows.</div></div>
          <div className="acc-card"><div className="acc-num">0.337</div><div className="acc-title">Best NMSE Recorded (GOOG)</div><div className="acc-desc">Normalized Mean Squared Error measures prediction tightness. A score of 0.337 over a two-week GOOG window reflects precision well within acceptable range.</div></div>
        </div>
      </div>
      <div className="home-section" style={{ paddingTop: 0 }}>
        <div className="section-label">{/* Verified Predictions */}</div>
        <h2 className="home-title">Live Case Studies</h2>
        <div className="cases-grid">
          {CASES.map(c => (
            <div className="case-card" key={c.asset}>
              <div className="case-tag">{c.tag}</div>
              <div style={{ fontSize: "9px", color: "var(--muted)", letterSpacing: "1px", marginBottom: "10px" }}>{c.period}</div>
              <div className="case-asset">{c.asset}</div>
              <div className="case-metrics">
                <div><div className="case-val pred">{c.pred}</div><div className="case-lbl">AI Forecast</div></div>
                <div><div className="case-val actual">{c.actual}</div><div className="case-lbl">Actual Result</div></div>
              </div>
              <div className="case-desc">{c.desc}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="home-section" style={{ paddingTop: 0 }}>
        <div className="section-label">{/* Community */}</div>
        <h2 className="home-title">20,000 Traders<br />Trust Figures AI</h2>
        <div className="testi-grid">
          {TESTIMONIALS.map(t => (
            <div className="testi-card" key={t.name}>
              <div className="testi-stars">★★★★★</div>
              <div className="testi-quote">"{t.q}"</div>
              <div className="testi-name">{t.name}</div>
              <div className="testi-role">{t.role}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="home-cta">
        <h2 className="cta-title">Start predicting with <span className="accent">Figures AI</span> today.</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", flexShrink: 0 }}>
          <button className="btn-primary" onClick={() => onTabChange("GUIDE")}>Begin Free Trial</button>
          <span style={{ fontSize: "10px", color: "var(--muted)", textAlign: "center", letterSpacing: "1px" }}>No credit card required</span>
        </div>
      </div>
    </div>
  );
}

/* ─── FORECASTS PAGE ─────────────────────────────────────────────────────── */
const rnd = (a, b) => Math.random() * (b - a) + a;
const fmtU = (n) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MODELS = [
  { name: "NN", layers: 5, neurons: 33, lr: "0.0244", base: 0.16 },
  { name: "SVM", layers: 5, neurons: 12, lr: "0.0022", base: 0.71 },
  { name: "GBOT", layers: 5, neurons: 17, lr: "0.0091", base: 0.50 },
  { name: "DBSCAM", layers: 4, neurons: 57, lr: "0.0075", base: 0.63 },
  { name: "HAC", layers: 3, neurons: 256, lr: "0.0082", base: 0.78 },
  { name: "Bagging", layers: 2, neurons: 154, lr: "0.0101", base: 0.47 },
];
const HIST_LEN = 100;

function PriceChart({ prices, upPct }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas || prices.length < 2) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width = canvas.offsetWidth, H = canvas.height = canvas.offsetHeight;
    ctx.clearRect(0, 0, W, H);
    const min = Math.min(...prices) - 5, max = Math.max(...prices) + 5;
    const sx = i => (i / (prices.length - 1)) * W;
    const sy = v => H - ((v - min) / (max - min)) * H;
    ctx.strokeStyle = "rgba(0,220,130,0.06)"; ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = (i / 4) * H; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      ctx.fillStyle = "rgba(0,220,130,0.3)"; ctx.font = "9px 'JetBrains Mono'";
      ctx.fillText(fmtU(max - (i / 4) * (max - min)), 4, y - 3);
    }
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, upPct >= 50 ? "rgba(0,220,130,0.16)" : "rgba(255,61,90,0.16)");
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.beginPath(); ctx.moveTo(sx(0), sy(prices[0]));
    prices.forEach((p, i) => ctx.lineTo(sx(i), sy(p)));
    ctx.lineTo(sx(prices.length - 1), H); ctx.lineTo(sx(0), H); ctx.closePath();
    ctx.fillStyle = g; ctx.fill();
    ctx.beginPath(); ctx.strokeStyle = upPct >= 50 ? "#00dc82" : "#ff3d5a"; ctx.lineWidth = 1.5; ctx.lineJoin = "round";
    prices.forEach((p, i) => i === 0 ? ctx.moveTo(sx(i), sy(p)) : ctx.lineTo(sx(i), sy(p))); ctx.stroke();
    const lx = sx(prices.length - 1), ly = sy(prices[prices.length - 1]);
    ctx.beginPath(); ctx.arc(lx, ly, 4, 0, Math.PI * 2);
    ctx.fillStyle = upPct >= 50 ? "#00dc82" : "#ff3d5a"; ctx.fill();
  });
  return <canvas ref={ref} className="chart-canvas" style={{ display: "block", width: "100%", height: "100%" }} />;
}

function ForecastsPage() {
  const [prices, setPrices] = useState(() => {
    let p = 67420, arr = [];
    for (let i = 0; i < HIST_LEN; i++) { p += rnd(-40, 40); arr.push(p); }
    return arr;
  });
  const [upPct, setUpPct] = useState(50);
  const [modelAccs, setModelAccs] = useState(MODELS.map(m => m.base + rnd(-0.04, 0.04)));
  const [trades, setTrades] = useState([]);
  const [activeTrade, setActiveTrade] = useState(null);
  const [mrnd, setMrnd] = useState(1);
  const [baseStake] = useState(10);
  const [lastRes, setLastRes] = useState({ rounds: "--", profit: "--" });
  const [wait, setWait] = useState(30);
  const [dir, setDir] = useState("up");

  // Use refs to hold latest values needed inside the wait-triggered effect
  // without making them reactive dependencies (avoids infinite loop)
  const upPctRef = useRef(upPct);
  const mrndRef = useRef(mrnd);
  const curRef = useRef(prices[prices.length - 1]);

  // Keep refs in sync
  useEffect(() => { upPctRef.current = upPct; }, [upPct]);
  useEffect(() => { mrndRef.current = mrnd; }, [mrnd]);
  useEffect(() => { curRef.current = prices[prices.length - 1]; }, [prices]);

  const cur = prices[prices.length - 1];
  const prev = prices[prices.length - 2] ?? cur;
  const dnPct = 100 - upPct;

  // Price + model ticker
  useEffect(() => {
    const id = setInterval(() => {
      setPrices(p => { const last = p[p.length - 1], nxt = Math.max(60000, last + rnd(-22, 22)); return [...p.slice(-HIST_LEN + 1), nxt]; });
      setModelAccs(p => p.map(a => Math.min(0.95, Math.max(0.3, a + rnd(-0.01, 0.01)))));
      setUpPct(rnd(38, 72));
    }, 400);
    return () => clearInterval(id);
  }, []);

  // Direction indicator — fix: add prev to deps
  useEffect(() => { setDir(cur >= prev ? "up" : "dn"); }, [cur, prev]);

  // Countdown timer
  useEffect(() => {
    const id = setInterval(() => setWait(c => c <= 1 ? 30 : c - 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Auto-trade logic — reads upPct/mrnd/cur via refs so they're not reactive deps
  useEffect(() => {
    if (wait !== 30) return;
    const currentUpPct = upPctRef.current;
    const currentMrnd = mrndRef.current;
    const orderPrice = curRef.current;

    if (currentUpPct > 55 || currentUpPct < 45) {
      const side = currentUpPct > 55 ? "BUY" : "SELL";
      const stake = baseStake * Math.pow(3, currentMrnd - 1);

      setTimeout(() => {
        setPrices(p => {
          const sp = p[p.length - 1];
          const won = (side === "BUY" && sp > orderPrice) || (side === "SELL" && sp < orderPrice);
          const profit = won ? +(stake * 0.95).toFixed(2) : -stake;
          setTrades(prev => [{ round: currentMrnd, time: new Date().toLocaleTimeString(), side, orderPrice: fmtU(orderPrice), settlePrice: fmtU(sp), profit }, ...prev].slice(0, 40));
          if (won) { setLastRes({ rounds: currentMrnd, profit: "+" + profit.toFixed(2) }); setMrnd(1); }
          else setMrnd(r => r + 1);
          setActiveTrade(null);
          return p;
        });
      }, 5000);

      setActiveTrade({ side, stake, orderPrice, time: new Date().toLocaleTimeString() });
    }
  }, [wait, baseStake]);

  const consensus = upPct >= 55 ? "LONG" : upPct <= 45 ? "SHORT" : "NEUTRAL";
  const avgAcc = modelAccs.reduce((a, b) => a + b, 0) / modelAccs.length;

  return (
    <div className="dash-wrap">
      <div className="dash-left">
        <div className="chart-wrap">
          <div className="chart-header">
            <span className="live-dot" />
            <span className="ch-title">BTC/USDT</span>
            <span className="ch-lbl">LIVE · 30s INTERVAL</span>
            <span style={{ marginLeft: "auto", fontSize: "9px", color: "var(--muted)", letterSpacing: "1px" }}>NEXT: {wait}s</span>
            <div style={{ marginLeft: "16px", display: "flex", gap: "8px", alignItems: "center" }}>
              <span className={`dash-price ${dir}`}>{fmtU(cur)}</span>
              <span className={`price-chg ${dir}`}>{dir === "up" ? "▲" : "▼"}</span>
            </div>
          </div>
          <PriceChart prices={prices} upPct={upPct} />
        </div>
        <div className="pred-section">
          <div className="pred-half">
            <div className="pred-half-title">▲ Probability of Rise</div>
            <div className="pred-pct up">{upPct.toFixed(1)}%</div>
            <div className="pred-bar-wrap"><div className="pred-bar-fill" style={{ width: `${upPct}%`, background: "var(--green)" }} /></div>
            <div className="pred-meta"><div><div className="pred-meta-val">0.55</div><div className="pred-meta-lbl">Avg Up</div></div><div><div className="pred-meta-val">0.31</div><div className="pred-meta-lbl">SD Up</div></div></div>
          </div>
          <div className="pred-half">
            <div className="pred-half-title">▼ Probability of Fall</div>
            <div className="pred-pct dn">{dnPct.toFixed(1)}%</div>
            <div className="pred-bar-wrap"><div className="pred-bar-fill" style={{ width: `${dnPct}%`, background: "var(--red)" }} /></div>
            <div className="pred-meta"><div><div className="pred-meta-val">0.49</div><div className="pred-meta-lbl">Avg Down</div></div><div><div className="pred-meta-val">0.37</div><div className="pred-meta-lbl">SD Down</div></div></div>
          </div>
        </div>
      </div>
      <div className="dash-right">
        <div className="signal-wrap">
          <div className={`signal-ring ${consensus === "LONG" ? "up" : consensus === "SHORT" ? "dn" : "neutral"}`}>{consensus}</div>
          <div>
            <div className="signal-main" style={{ color: consensus === "LONG" ? "var(--green)" : consensus === "SHORT" ? "var(--red)" : "var(--muted)" }}>
              {consensus === "NEUTRAL" ? "Awaiting signal..." : `Figures AI: ${consensus}`}
            </div>
            <div className="signal-sub">Ensemble confidence: {(avgAcc * 100).toFixed(1)}%<br />Next 30s window</div>
          </div>
        </div>
        <div className="model-section">
          <div className="section-hdr"><span className="section-hdr-title">Model Ensemble</span></div>
          <table className="model-table">
            <thead><tr><th>Model</th><th>Layers</th><th>N</th><th>LR</th><th>Acc</th></tr></thead>
            <tbody>
              {MODELS.map((m, i) => {
                const v = modelAccs[i]; const cls = v >= 0.65 ? "hi" : v >= 0.5 ? "mid" : "lo";
                return <tr className="model-row" key={m.name}><td>{m.name}</td><td>{m.layers}</td><td>{m.neurons}</td><td>{m.lr}</td><td><span className={`acc-pill ${cls}`}>{(v * 100).toFixed(0)}%</span></td></tr>;
              })}
            </tbody>
          </table>
        </div>
        <div className="trade-section">
          <div className="trade-current">
            <div className="section-hdr" style={{ padding: "0 0 8px", background: "transparent", border: "none" }}><span className="section-hdr-title">Martingale Simulator</span></div>
            <div className="trade-kv"><span className="trade-k">Round</span><span className="trade-v">{mrnd} · ${(baseStake * Math.pow(3, mrnd - 1)).toFixed(0)}</span></div>
            <div className="trade-kv"><span className="trade-k">Last Cycle</span><span className={`trade-v ${String(lastRes.profit).startsWith("+") ? "up" : "dn"}`}>{lastRes.rounds} rnd · {lastRes.profit}</span></div>
            <div className="trade-kv"><span className="trade-k">Active</span><span className={`trade-v ${activeTrade ? (activeTrade.side === "BUY" ? "up" : "dn") : ""}`}>{activeTrade ? `${activeTrade.side} @ ${fmtU(activeTrade.orderPrice)}` : "Watching..."}</span></div>
            <div className="trade-btn-row"><button className="trade-btn buy" disabled>BUY ▲</button><button className="trade-btn sell" disabled>SELL ▼</button></div>
            <div style={{ fontSize: "8px", color: "var(--muted)", marginTop: "6px", letterSpacing: "1px" }}>AUTO · Threshold &gt;55% · 3× martingale</div>
          </div>
          <div className="history-section">
            <div className="section-hdr"><span className="section-hdr-title">Trade History</span></div>
            <div className="history-scroll">
              <table className="htable">
                <thead><tr><th>Rnd</th><th>Time</th><th>Side</th><th>Order</th><th>P/L</th></tr></thead>
                <tbody>
                  {trades.length === 0 && <tr><td colSpan={5} style={{ color: "var(--muted)", textAlign: "center", padding: "20px 0", fontSize: "9px", letterSpacing: "2px" }}>AWAITING SIGNALS...</td></tr>}
                  {trades.map((t, i) => <tr key={i}><td>{t.round}</td><td>{t.time}</td><td className={t.side === "BUY" ? "side-buy" : "side-sell"}>{t.side}</td><td>{t.orderPrice}</td><td className={`profit ${t.profit >= 0 ? "up" : "dn"}`}>{t.profit >= 0 ? "+" : ""}{t.profit.toFixed(2)}</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── LEARN PAGE ─────────────────────────────────────────────────────────── */
const CATEGORIES = [
  { id: "all", label: "All Books" },
  { id: "ai", label: "Artificial Intelligence" },
  { id: "quant", label: "Quantitative Trading" },
  { id: "ml", label: "Machine Learning" },
  { id: "stats", label: "Statistics" },
];
const BOOKS = [
  { id: 1, title: "Artificial Intelligence: A Modern Approach", edition: "Global Edition", author: "Stuart Russell & Peter Norvig", category: "ai", level: "intermediate", color: "#00dc82", tags: ["Intelligent Agents", "Search Algorithms", "NLP", "ML"], desc: "The definitive AI textbook — covering foundational theory from intelligent agents and search to machine learning and NLP, illustrated with real-world industry applications. Accessible without a deep technical background." },
  { id: 2, title: "Inside the Black Box", edition: "The Simple Truth About Quantitative Trading", author: "Rishi Narang", category: "quant", level: "beginner", color: "#ffc93c", tags: ["Quant Strategies", "Fund Management", "Black Box", "Systematic Trading"], desc: "Expert fund manager Rishi Narang dismantles the mystery around quantitative trading, showing through real-world examples that what many call a 'black box' is transparent, intuitive, and learnable." },
  { id: 3, title: "Algorithmic Trading and DMA", edition: "Direct Access Trading Strategies", author: "Barry Johnson", category: "quant", level: "advanced", color: "#0af", tags: ["DMA", "Market Microstructure", "VWAP", "TWAP", "Transaction Costs"], desc: "A comprehensive ground-up reference for algorithmic execution and direct market access, covering order types, VWAP/TWAP algorithms, transaction cost analysis, and AI integration for institutional desks." },
  { id: 4, title: "Algorithmic Trading", edition: "Winning Strategies and Their Rationale", author: "Ernest P. Chan", category: "quant", level: "intermediate", color: "#ff3d5a", tags: ["Systematic Strategies", "Backtesting", "Live Trading", "Coding"], desc: "Practitioner-authored guide that brings quantitative strategies to life with real implementations, actual code, and honest rationale for every design decision. Essential for building a systematic trading business." },
  { id: 5, title: "Trading and Exchanges", edition: "Market Microstructure for Practitioners", author: "Larry Harris", category: "quant", level: "intermediate", color: "#a855f7", tags: ["Market Microstructure", "Order Types", "Exchanges", "Arbitrage"], desc: "A panoramic look at the full trading ecosystem — every participant, every venue, every mechanism — providing practitioners with structural insight into why markets behave as they do." },
  { id: 6, title: "Quantitative Trading", edition: "Build Your Own Algorithmic Trading Business", author: "Dr. Ernest Chan", category: "quant", level: "beginner", color: "#00dc82", tags: ["Independent Trading", "Strategy Development", "Risk Management"], desc: "Dr. Chan proves independent traders can compete with institutional quants — then shows every step required to launch a quantitative trading business, from strategy selection to risk management and scaling." },
  { id: 7, title: "Deep Learning", edition: "Foundational Principles & Applications", author: "Goodfellow, Bengio & Courville", category: "ml", level: "advanced", color: "#ffc93c", tags: ["Neural Networks", "CNNs", "NLP", "Generative Models"], desc: "The authoritative graduate-level text on deep learning. Mathematical foundations meet feedforward networks, regularization, optimization, convolutional and recurrent architectures, and generative models." },
  { id: 8, title: "The Elements of Statistical Learning", edition: "Data Mining, Inference & Prediction — 2nd Ed.", author: "Hastie, Tibshirani & Friedman", category: "stats", level: "advanced", color: "#0af", tags: ["Statistical Learning", "SVMs", "Random Forests", "Boosting"], desc: "A landmark reference spanning medicine, biology, finance, and marketing — unifying supervised and unsupervised learning under one framework. Adds random forests, ensemble methods, spectral clustering, and more." },
  { id: 9, title: "Pattern Recognition and Machine Learning", edition: "Bayesian Perspective", author: "Christopher M. Bishop", category: "ml", level: "advanced", color: "#ff3d5a", tags: ["Bayesian Methods", "Probabilistic Models", "Graphical Models"], desc: "The first pattern recognition textbook adopting a fully Bayesian viewpoint, developing graphical models and approximate inference algorithms with a self-contained probability primer included." },
  { id: 10, title: "Hands-On Machine Learning", edition: "Scikit-Learn and TensorFlow", author: "Aurélien Géron", category: "ml", level: "beginner", color: "#a855f7", tags: ["Scikit-Learn", "TensorFlow", "Neural Networks", "Deep RL"], desc: "Hands-on journey from basic linear regression to deep neural networks using two production-ready Python frameworks. Minimal theory, maximum intuition, concrete examples, and chapter exercises throughout." },
];
const LEVEL_COLORS = { beginner: "#00dc82", intermediate: "#ffc93c", advanced: "#ff3d5a" };
const LEVEL_LABELS = { beginner: "Foundational", intermediate: "Intermediate", advanced: "Advanced" };

function LearnPage() {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const filtered = filter === "all" ? BOOKS : BOOKS.filter(b => b.category === filter);
  return (
    <div style={{ position: "relative", zIndex: 1 }}>
      <div className="learn-wrap">
        <div className="learn-hero">
          <div className="eyebrow">Knowledge Library</div>
          <h1 className="learn-title">Master the Markets<br /><span style={{ color: "rgba(200,232,216,.2)" }}>&amp; the Machines.</span></h1>
          <p className="learn-sub">A curated selection of essential texts spanning quantitative trading, algorithmic execution, machine learning, and AI — chosen to build the intellectual foundation every data-driven trader needs.</p>
          <div className="filter-bar">
            {CATEGORIES.map(c => (
              <button key={c.id} className={`filter-btn ${filter === c.id ? "active" : ""}`} onClick={() => setFilter(c.id)}>{c.label}</button>
            ))}
          </div>
        </div>
        <div className="count-bar">
          <span className="count-num">{String(filtered.length).padStart(2, "0")}</span>
          <span className="count-lbl">Titles in selection</span>
          <span style={{ marginLeft: "auto", fontSize: "9px", color: "var(--muted)", letterSpacing: "1px" }}>{CATEGORIES.find(c => c.id === filter)?.label.toUpperCase()}</span>
        </div>
        <div style={{ background: "var(--border)", marginTop: "2px" }}>
          <div className="books-grid">
            {filtered.map((book, idx) => (
              <div className="book-card" key={book.id} onClick={() => setSelected(book)}>
                <div className="book-spine" style={{ background: book.color }} />
                <div className="book-inner">
                  <div className="book-meta">
                    <span className="book-cat" style={{ borderColor: book.color + "55", color: book.color, background: book.color + "15" }}>
                      {book.category === "ai" ? "AI" : book.category === "quant" ? "Quant" : book.category === "ml" ? "ML" : "Stats"}
                    </span>
                    <span className="book-num">#{String(idx + 1).padStart(2, "0")}</span>
                  </div>
                  <div><div className="book-title">{book.title}</div><div className="book-author" style={{ marginTop: "5px" }}>{book.author}</div></div>
                  <div className="book-desc">{book.desc}</div>
                </div>
                <div className="book-footer">
                  <button className="book-read-btn" style={{ background: book.color + "22", borderColor: book.color + "55", color: book.color }} onClick={e => { e.stopPropagation(); setSelected(book); }}>Read More →</button>
                  <div className="book-level"><div className="level-dot" style={{ background: LEVEL_COLORS[book.level] }} />{LEVEL_LABELS[book.level]}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-spine" style={{ background: selected.color }} />
            <div className="modal-header">
              <span style={{ fontSize: "8px", letterSpacing: "2px", textTransform: "uppercase", padding: "4px 10px", border: "1px solid", borderColor: selected.color + "55", color: selected.color, background: selected.color + "15", fontFamily: "var(--ff-mono)" }}>
                {selected.category === "ai" ? "Artificial Intelligence" : selected.category === "quant" ? "Quantitative Trading" : selected.category === "ml" ? "Machine Learning" : "Statistics"}
              </span>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="modal-title">{selected.title}</div>
              <div style={{ fontSize: "10px", color: "var(--muted2)", marginBottom: "4px" }}>{selected.edition}</div>
              <div className="modal-author">{selected.author}</div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px", padding: "9px 12px", background: "rgba(0,0,0,.3)", border: "1px solid var(--border)" }}>
                <div className="level-dot" style={{ background: LEVEL_COLORS[selected.level], width: "7px", height: "7px", borderRadius: "50%" }} />
                <span style={{ fontSize: "9px", color: "var(--muted2)", letterSpacing: "2px", textTransform: "uppercase" }}>{LEVEL_LABELS[selected.level]} Level</span>
              </div>
              <div className="modal-desc">{selected.desc}</div>
              <div className="modal-tags">{selected.tags.map(t => <span key={t} className="modal-tag">{t}</span>)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── API PAGE ───────────────────────────────────────────────────────────── */
const API_FEATURES = ["Real-Time Price Feeds", "AI Prediction Signals", "30s Interval Forecasts", "Multi-Asset Coverage", "Martingale Strategy Engine", "Historical Model Data"];
const API_LINES = [
  { el: <><span className="t-green">GET</span> <span className="t-blue">/v1/predict/BTC-USDT</span></>, delay: 0 },
  { el: <><span style={{ color: "var(--muted)" }}>Authorization:</span> Bearer <span className="t-green">••••••••••••••</span></>, delay: 200 },
  { el: <><span style={{ color: "var(--muted)" }}>X-Model:</span> ensemble-v3</>, delay: 400 },
  { el: null, delay: 600 },
  { el: <span className="t-locked">⛔ 403 FORBIDDEN — Full API access requires subscription</span>, delay: 800 },
];

function APIPage() {
  const [showModal, setShowModal] = useState(true);
  const [typed, setTyped] = useState(0);
  useEffect(() => {
    API_LINES.forEach((_, i) => setTimeout(() => setTyped(i + 1), 500 + API_LINES[i].delay));
  }, []);
  return (
    <div>
      <div className="api-wrap">
        <div className="lock-wrap"><span className="lock-icon">🔒</span></div>
        <div className="eyebrow" style={{ justifyContent: "center" }}>Full API Access</div>
        <h1 className="api-headline">Unlock the <span className="dim">Full</span><br />Prediction Engine</h1>
        <p className="api-subtext">The Figures AI API grants programmatic access to our ensemble prediction models, real-time signal feeds, and historical forecast data. Contact our team to activate your subscription.</p>
        <div className="feat-pills">
          {API_FEATURES.map(f => <div className="feat-pill" key={f}><div className="feat-dot" />{f}</div>)}
        </div>
        <div className="terminal">
          <div className="terminal-bar">
            <div className="t-dot" style={{ background: "#ff3d5a" }} />
            <div className="t-dot" style={{ background: "#ffc93c" }} />
            <div className="t-dot" style={{ background: "#00dc82" }} />
            <span className="t-title">API Request Preview</span>
          </div>
          <div className="terminal-body">
            {API_LINES.slice(0, typed).map((l, i) =>
              l.el === null ? <div key={i} style={{ height: "8px" }} /> : <div key={i}>{l.el}</div>
            )}
          </div>
        </div>
        <button className="api-cta" onClick={() => setShowModal(true)}>Contact Support to Unlock →</button>
      </div>
      {showModal && (
        <div className="api-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="api-modal" onClick={e => e.stopPropagation()}>
            <div className="api-modal-accent" />
            <button className="modal-close-x" onClick={() => setShowModal(false)}>✕</button>
            <div className="api-modal-inner">
              <div className="api-modal-icon">📡</div>
              <div className="api-modal-title">Please contact our <span className="accent">customer support team</span> to unlock the full version.</div>
              <div className="api-modal-desc">Our team will verify your account and activate full API access, including real-time prediction signals, ensemble model outputs, and priority rate limits.</div>
              <div className="contact-row"><div className="contact-icon">✉️</div><div><div className="contact-label">Email Support</div><div className="contact-val">support@figures.ai</div></div></div>
              <div className="contact-row"><div className="contact-icon">💬</div><div><div className="contact-label">Live Chat</div><div className="contact-val">Available 24/7 via dashboard</div></div></div>
              <button className="api-ok-btn" onClick={() => setShowModal(false)}>OK — Got It</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── GUIDE PAGE ─────────────────────────────────────────────────────────── */
const STEPS = [
  {
    tag: "Step 01", title: "Account Setup", rows: [
      { icon: "🏦", label: "Platform", val: <><span className="accent"><a className="accent"  href="https://www.cryptexpro.app/">CryptexPro</a></span> — create your account at the exchange</> },
      { icon: "🛡️", label: "Security", val: "Complete all KYC / identity verification steps to secure your account and enable withdrawals" },
    ],
    tip: "Use a strong unique password and enable two-factor authentication immediately after registration.",
  },
  {
    tag: "Step 02", title: "Fund Your Wallet", rows: [
      { icon: "💵", label: "Minimum Deposit", val: <span style={{ fontFamily: "var(--ff-display)", fontSize: "18px", fontWeight: 900, color: "var(--green)" }}>$100</span> },
      { icon: "📌", label: "Why This Amount", val: "This is the baseline capital required to activate and run the AI copy trading feature on your account" },
    ],
    tip: "Only deposit capital you are prepared to risk. Past model performance does not guarantee future returns.",
  },
  {
    tag: "Step 03", title: "Connect with the AI Trader", rows: [
      { icon: "📍", label: "Location", val: <>Navigate to the <span className="accent">Copy Trading</span> section of your CryptexPro dashboard</> },
      { icon: "🔍", label: "Search For", val: <><span className="highlight">Alex Krumar</span> — the designated Figures AI signal trader</> },
      { icon: "✅", label: "Action", val: <>Select <span className="accent">"Copy"</span> to sync your wallet with the trader's live positions</> },
    ],
    tip: "Verify the trader profile matches exactly before confirming — check the name and stats carefully.",
  },
  {
    tag: "Step 04", title: "Daily Maintenance", rows: [
      { icon: "⏰", label: "Daily Check-In", val: <><span className="highlight">11:00 AM GMT+1</span> — every day without exception</> },
      { icon: "📋", label: "Action", val: "Manually copy the available signal from the trader to keep your positions aligned with the latest AI forecast" },
    ],
    tip: "Missing a daily sync may cause your positions to fall out of alignment with current model signals.",
  },
];

function GuidePage() {
  const [done, setDone] = useState([false, false, false, false]);
  const toggle = i => setDone(p => p.map((v, idx) => idx === i ? !v : v));
  const allDone = done.every(Boolean);
  const activeStep = done.findIndex(d => !d);
  return (
    <div className="guide-wrap">
      <div className="eyebrow">Getting Started</div>
      <h1 className="guide-title">AI Copy Trading<br /><span style={{ color: "rgba(200,232,216,.2)" }}>Setup Guide</span></h1>
      <p className="guide-sub">Follow these four steps to connect your exchange account to the Figures AI prediction engine and begin copy trading in under 10 minutes.</p>
      <div className="progress-row">
        {STEPS.map((s, i) => (
          <div className="prog-step" key={i} onClick={() => toggle(i)}>
            <div className={`prog-node ${done[i] ? "done" : i === activeStep ? "active" : "idle"}`}>{done[i] ? "✓" : `0${i + 1}`}</div>
            <span className={`prog-label ${i === activeStep && !done[i] ? "active" : ""}`}>{s.tag}</span>
            {i < STEPS.length - 1 && <div className={`prog-line ${done[i] ? "done" : ""}`} />}
          </div>
        ))}
      </div>
      <div className="steps">
        {STEPS.map((step, i) => (
          <div className={`step-card ${i === activeStep && !done[i] ? "active-card" : ""}`} key={i}>
            <div className="step-num-col">
              <div className={`step-circle ${done[i] ? "done" : i === activeStep ? "active" : "idle"}`}>{done[i] ? "✓" : `0${i + 1}`}</div>
            </div>
            <div className="step-body">
              <div className="step-tag">{step.tag}</div>
              <div className="step-title">{step.title}</div>
              <div className="info-grid">
                {step.rows.map((row, r) => (
                  <div className="info-row" key={r}>
                    <div className="info-icon">{row.icon}</div>
                    <div><div className="info-label">{row.label}</div><div className="info-val">{row.val}</div></div>
                  </div>
                ))}
              </div>
              <div className="tip-box"><span className="tip-icon">💡</span><span>{step.tip}</span></div>
              <button className={`mark-btn ${done[i] ? "undo-btn" : "done-btn"}`} onClick={() => toggle(i)}>
                {done[i] ? "↩ Mark as Incomplete" : "✓ Mark Step Complete"}
              </button>
            </div>
          </div>
        ))}
      </div>
      {allDone && (
        <div className="complete-banner">
          <span style={{ fontSize: "26px" }}>🚀</span>
          <div><div className="complete-text">Setup Complete — You're Live.</div><div className="complete-sub">Check back at 11:00 AM GMT+1 daily for the latest signal.</div></div>
        </div>
      )}
      <div className="summary">
        {[["Exchange Platform","CryptexPro"],["Minimum Capital","$100 USD"],["AI Trader to Follow","Alex Krumar"],["Daily Signal Time","11:00 AM GMT+1"]].map(([k,v]) => (
          <div key={k}><div className="sum-k">{k}</div><div className="sum-v">{v}</div></div>
        ))}
      </div>
      <div className="about-section">
        <div className="about-label">{/* About This Guide */}</div>
        <div className="about-title">How AI Copy Trading Works</div>
        <div className="trader-card">
          <div className="trader-avatar">🤖</div>
          <div><div className="trader-name">Alex Krumar</div><div className="trader-sub">Figures AI — Designated Signal Trader</div></div>
          <div className="trader-stats">
            <div><div className="tstat-val">30s</div><div className="tstat-lbl">Interval</div></div>
            <div><div className="tstat-val">90%+</div><div className="tstat-lbl">Accuracy</div></div>
            <div><div className="tstat-val">Daily</div><div className="tstat-lbl">Sync</div></div>
          </div>
        </div>
        <div className="clock-strip">
          <span style={{ fontSize: "18px" }}>🕐</span>
          <div><div className="clock-time">11:00 AM GMT+1</div><div className="clock-text">Daily signal window — manual copy required each session</div></div>
        </div>
        <div className="about-body">
          <p>Figures AI's copy trading system connects your CryptexPro wallet to an AI-driven signal trader. The prediction model monitors global market data and generates 30-second directional signals using an ensemble of six machine learning architectures.</p>
          <p>The designated trader — <span style={{ color: "var(--green)", fontWeight: 600 }}>Alex Krumar</span> — acts as the conduit between model output and your live positions. Copying this trader inside CryptexPro automatically mirrors positions as they execute.</p>
          <p>A brief daily check-in at <span style={{ color: "var(--blue)" }}>11:00 AM GMT+1</span> is required to pull the latest signal and confirm positions are synchronized with current model outputs.</p>
          <p style={{ color: "rgba(200,232,216,.3)", fontSize: "11px" }}>⚠ For informational and educational purposes only. Copy trading involves financial risk. Past model performance does not guarantee future results.</p>
        </div>
      </div>
    </div>
  );
}

/* ─── CONTACT PAGE ───────────────────────────────────────────────────────── */
const CHANNELS = [
  {
    cls: "telegram",
    icon: "✈️",
    name: "Telegram",
    handle: "@cryptexapp",
    url: "https://t.me/cryptexapp",
    desc: "Join our active Telegram community for real-time trade signals, market alerts, and direct support from the Figures AI team — available around the clock.",
    cta: "Join Channel →",
  },
  {
    cls: "discord",
    icon: "🎮",
    name: "Discord",
    handle: "discord.gg/XtvubjS2M",
    url: "https://discord.gg/XtvubjS2M",
    desc: "Connect with thousands of traders on our Discord server. Share strategies, discuss AI signals, and get fast answers from our moderation and support team.",
    cta: "Join Server →",
  },
  {
    cls: "facebook",
    icon: "📘",
    name: "Facebook",
    handle: "Figures AI Community",
    url: "https://www.facebook.com/share/19snmj27Dq/?mibextid=wwXIfr",
    desc: "Follow our Facebook page for trading education, platform updates, and community stories. A great space for traders who prefer a familiar social environment.",
    cta: "Visit Page →",
  },
];

const STATS = [
  { num: ">2M", lbl: "Trades Executed" },
  { num: ">100M", lbl: "Data Points Processed" },
  { num: ">100 TFLOPs", lbl: "Compute Power" },
  { num: ">20,000", lbl: "Active Users" },
];

const FAQS = [
  { q: "How do I get started with AI copy trading?", a: "Head to the GUIDE tab for a full step-by-step walkthrough. You'll need a CryptexPro account, a minimum deposit of $100, and to follow the AI trader Alex Krumar in the Copy Trading section." },
  { q: "What is the minimum amount required to use the platform?", a: "The minimum capital required to activate the AI copy trading feature is $100 USD. This is the baseline needed to initiate and run positions through the system." },
  { q: "How accurate are the AI predictions?", a: "Our ensemble model exceeds 90% directional accuracy on short-term price trends across forex, crypto, and equities. Individual case studies — including ASML, BTC/USD, and GOOG — are documented in the FORECASTS section." },
  { q: "When should I sync my signals each day?", a: "Check in daily at 11:00 AM GMT+1 and manually copy the available signal from Alex Krumar to ensure your positions are aligned with the latest model output." },
  { q: "How do I access the full version of the prediction API?", a: "The full API requires a subscription. Contact our support team via Telegram, Discord, or email at support@figures.ai — our team will verify your account and activate access." },
  { q: "Is this financial advice?", a: "No. All content on Figures AI is for educational and informational purposes only. Predictions are simulated references. Past model performance does not guarantee future returns. Always trade responsibly." },
];

function ContactPage() {
  const [openFaq, setOpenFaq] = useState(null);
  return (
    <div className="contact-wrap">

      <div className="contact-hero">
        <div className="eyebrow">Support &amp; Community</div>
        <h1 className="contact-title">Support Is Just<br /><span style={{ color: "rgba(200,232,216,.2)" }}>a Click Away</span></h1>
        <p className="contact-sub">
          Have questions or need assistance with Figures AI? Our customer support team is here to help. Whether you need guidance on features, want to report an issue, or simply want to connect with other traders — choose your preferred channel below.
        </p>
      </div>

      <div className="contact-stats">
        {STATS.map(s => (
          <div className="cstat-card" key={s.lbl}>
            <div className="cstat-num">{s.num}</div>
            <div className="cstat-lbl">{s.lbl}</div>
          </div>
        ))}
      </div>

      <div className="section-label">{/* Choose Your Channel */}</div>
      <div className="contact-grid">
        {CHANNELS.map(ch => (
          <a
            key={ch.cls}
            className={`channel-card ${ch.cls}`}
            href={ch.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="channel-icon-wrap">{ch.icon}</div>
            <div>
              <div className="channel-name">{ch.name}</div>
              <div className="channel-handle">{ch.handle}</div>
            </div>
            <div className="channel-desc">{ch.desc}</div>
            <div className="channel-cta">{ch.cta}</div>
          </a>
        ))}
      </div>

      <div className="email-strip">
        <div className="email-strip-left">
          <div className="email-strip-label">{/* Direct Email Support */}</div>
          <div className="email-strip-title">Prefer to write to us directly?</div>
          <div className="email-addr">support@figures.ai</div>
        </div>
        <a href="mailto:support@figures.ai">
          <button className="email-btn">Send Email →</button>
        </a>
      </div>

      <div className="faq-section">
        <div className="section-label">{/* Frequently Asked Questions */}</div>
        <div className="faq-title">Common Questions</div>
        <div className="faq-list">
          {FAQS.map((f, i) => (
            <div className="faq-item" key={i}>
              <div className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <div className="faq-q-text">{f.q}</div>
                <div className={`faq-chevron ${openFaq === i ? "open" : ""}`}>▼</div>
              </div>
              {openFaq === i && <div className="faq-a">{f.a}</div>}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

/* ─── ROOT APP ───────────────────────────────────────────────────────────── */
export default function App() {
  const [tab, setTab] = useState("HOME");

  const renderPage = () => {
    switch (tab) {
      case "HOME":      return <HomePage onTabChange={setTab} />;
      case "FORECASTS": return <ForecastsPage />;
      case "LEARN":     return <LearnPage />;
      case "API":       return <APIPage />;
      case "GUIDE":     return <GuidePage />;
      case "CONTACT":   return <ContactPage />;
      default:          return <HomePage onTabChange={setTab} />;
    }
  };

  return (
    <div>
      <style>{SHARED_CSS}</style>
      <div className="bg-grid" />
      <div className="bg-glow" />
      <div className="bg-glow2" />

      <Header activeTab={tab} onTabChange={setTab} />

      <main>{renderPage()}</main>

      {tab !== "FORECASTS" && <Footer onTabChange={setTab} />}
    </div>
  );
}